import React from "react";
import { NumericFormat, SourceInfo } from "react-number-format";
import { TextField } from "@mui/material";
import DKFormInputLabelContainer from "./formInputContainer/DKFormInputLabelContainer";

export type DKFormNumberInputProps = {
  dataTestId?: string;
  disabled?: boolean;
  name: string;
  label: string;
  required?: boolean;
  maxDecimalPlaces?: number;
  value: number;
  onChange?: (value: number, sourceInfo: SourceInfo) => void;
  startAdornment?: React.JSX.Element;
  endAdornment?: React.JSX.Element;
};

const DKFormNumberInput = (props: DKFormNumberInputProps) => {
  const {
    dataTestId = "",
    disabled = false,
    name,
    label = "",
    required = false,
    maxDecimalPlaces = 2,
    value,
    onChange,
    startAdornment,
    endAdornment,
  } = props;

  const [isInputStarted, setIsInputStarted] = React.useState(false);
  const [isInputFinished, setIsInputFinished] = React.useState(true);

  const isError = required && value === undefined && isInputStarted;

  const inputProps: {
    startAdornment?: React.JSX.Element;
    endAdornment?: React.JSX.Element;
  } = {};
  inputProps.startAdornment = startAdornment
    ? { ...startAdornment }
    : undefined;
  inputProps.endAdornment = endAdornment ? { ...endAdornment } : undefined;

  const materialUITextFieldProps = {
    datatestid: dataTestId,
    name: name,
    required: required,
    variant: "outlined" as const,
    error: isError,
    helperText: isError ? `${label} is required` : "",
    InputProps: inputProps,
  };

  const formatValue = () => {
    //calculate number of decimal places
    const numberOfDecimals = value?.toString().split(".")[1]?.length || 0;

    //format value to 2 decimal places if needed when input is finished

    if (value && numberOfDecimals <= 2 && isInputFinished) {
      return value.toFixed(2);
    } else if (value && numberOfDecimals > 2 && isInputFinished) {
      return value.toFixed(numberOfDecimals);
    } else {
      return value?.toFixed(numberOfDecimals);
    }
  };

  const formattedValue = formatValue();

  return (
    <DKFormInputLabelContainer label={label}>
      <NumericFormat
        disabled={disabled}
        data-testid={dataTestId}
        value={formattedValue}
        displayType="input"
        decimalScale={maxDecimalPlaces}
        thousandSeparator=","
        allowNegative={false}
        onValueChange={(values, sourceInfo: SourceInfo) => {
          //set input finished to false when user starts typing for use in formatting value
          setIsInputFinished(false);

          //set input started to true when user starts typing for use in enabling error checking
          setIsInputStarted(true);

          //set value to updated value
          if (onChange) {
            onChange(values.floatValue, sourceInfo);
          }

          if (sourceInfo.source == "prop") {
            setIsInputFinished(true);
            formatValue();
          }
        }}
        onBlur={() => {
          setIsInputFinished(true);
        }}
        customInput={TextField}
        {...materialUITextFieldProps}
        sx={{ width: "100%" }}
      />
    </DKFormInputLabelContainer>
  );
};

export default DKFormNumberInput;
